import React, { useState, useCallback, useMemo, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Users, GraduationCap, BookOpen, School, CalendarRange,
  Download, Upload, FileText, FileSpreadsheet, FileType,
  CheckCircle2, XCircle, AlertTriangle, Loader2, ArrowLeft, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const MODULES = [
  { key: "santri", label: "Data Santri", icon: Users, hasFilters: ["tahun_ajaran", "kelas", "status", "jenis_kelamin"] },
  { key: "ustadz", label: "Data Ustadz", icon: GraduationCap, hasFilters: ["status"] },
  { key: "mata_pelajaran", label: "Mata Pelajaran", icon: BookOpen, hasFilters: ["status"] },
  { key: "kelas", label: "Data Kelas", icon: School, hasFilters: ["tahun_ajaran"] },
  { key: "tahun_ajaran", label: "Tahun Ajaran", icon: CalendarRange, hasFilters: [] },
];

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

function parseFilenameFromCD(cd, fallback) {
  if (!cd) return fallback;
  const m = /filename="([^"]+)"/i.exec(cd) || /filename=([^;]+)/i.exec(cd);
  return m ? m[1].trim() : fallback;
}

export default function ImportExportPage() {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [tahunList, setTahunList] = useState([]);
  const [kelasList, setKelasList] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [ta, kl] = await Promise.all([
          api.get("/tahun-ajaran"),
          api.get("/kelas"),
        ]);
        setTahunList(ta.data.items || []);
        setKelasList(kl.data.items || []);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  const canAccess = user?.role === "admin" || user?.role === "sekretaris";
  if (!canAccess) {
    return (
      <div className="p-6 text-center text-slate-500" data-testid="ie-forbidden">
        Anda tidak memiliki akses ke fitur Import/Export.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="import-export-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 flex items-center gap-3">
            <FileSpreadsheet className="h-7 w-7 text-emerald-600" />
            Import &amp; Export Data
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Kelola data massal SiMadin dalam sekali klik. Template Excel resmi tersedia untuk setiap modul —
            unduh, isi, unggah, dan tinjau sebelum disimpan. Data lama tidak akan dihapus.
          </p>
        </div>
        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1.5 py-1.5">
          <ShieldCheck className="h-3.5 w-3.5" /> Admin &amp; Sekretaris
        </Badge>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {MODULES.map((m) => (
          <ModuleCard key={m.key} module={m} onOpen={() => setActive(m)} />
        ))}
      </div>

      {active && (
        <ModuleWorkspace
          module={active}
          onClose={() => setActive(null)}
          tahunList={tahunList}
          kelasList={kelasList}
        />
      )}
    </div>
  );
}

function ModuleCard({ module, onOpen }) {
  const Icon = module.icon;
  return (
    <Card
      className="p-5 border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all cursor-pointer bg-white"
      onClick={onOpen}
      data-testid={`ie-card-${module.key}`}
    >
      <div className="flex items-start justify-between">
        <div className="h-11 w-11 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
          <Icon className="h-5 w-5 text-emerald-700" />
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">Modul</span>
      </div>
      <div className="mt-3">
        <div className="font-heading text-lg font-bold text-slate-900">{module.label}</div>
        <div className="text-xs text-slate-500 mt-1">Template · Import · Export</div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">.xlsx</span>
        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">.csv</span>
        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">.pdf</span>
      </div>
    </Card>
  );
}

function ModuleWorkspace({ module, onClose, tahunList, kelasList }) {
  const [busy, setBusy] = useState(null); // 'template' | 'export' | 'preview' | 'import'
  const [previewData, setPreviewData] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [onDuplicate, setOnDuplicate] = useState("skip");
  const [exportFormat, setExportFormat] = useState("xlsx");
  const [exportFilters, setExportFilters] = useState({});
  const [importResult, setImportResult] = useState(null);
  const inputRef = React.useRef(null);

  const closeAll = () => {
    setPreviewData(null);
    setSelectedFile(null);
    setImportResult(null);
    onClose();
  };

  const downloadTemplate = async () => {
    setBusy("template");
    try {
      const res = await api.get(`/import-export/${module.key}/template`, { responseType: "blob" });
      saveBlob(res.data, parseFilenameFromCD(res.headers["content-disposition"], `Template_${module.key}.xlsx`));
      toast.success("Template berhasil diunduh");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal mengunduh template");
    } finally {
      setBusy(null);
    }
  };

  const doExport = async () => {
    setBusy("export");
    try {
      const params = { format: exportFormat, ...exportFilters };
      Object.keys(params).forEach((k) => (params[k] === "" || params[k] === "all") && delete params[k]);
      const res = await api.get(`/import-export/${module.key}/export`, {
        params, responseType: "blob",
      });
      const stamp = new Date().toISOString().slice(0, 10);
      const ext = exportFormat;
      const fname = parseFilenameFromCD(res.headers["content-disposition"], `${module.key}_${stamp}.${ext}`);
      saveBlob(res.data, fname);
      toast.success("Export berhasil");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal export");
    } finally {
      setBusy(null);
    }
  };

  const onFilePicked = async (file) => {
    setSelectedFile(file);
    setPreviewData(null);
    setImportResult(null);
    if (!file) return;
    setBusy("preview");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/import-export/${module.key}/preview`, fd);
      setPreviewData(data);
      if (data.invalid > 0) {
        toast.warning(`Ada ${data.invalid} baris bermasalah dari total ${data.total_rows}`);
      } else if (data.duplicates > 0) {
        toast.info(`${data.duplicates} baris sudah ada di database`);
      } else {
        toast.success(`Semua ${data.valid} baris valid & siap diimpor`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal preview file");
    } finally {
      setBusy(null);
    }
  };

  const runImport = async () => {
    if (!selectedFile) return;
    if (previewData && previewData.invalid > 0) {
      toast.error("Perbaiki dulu semua baris bermasalah sebelum import");
      return;
    }
    setBusy("import");
    try {
      const fd = new FormData();
      fd.append("file", selectedFile);
      fd.append("on_duplicate", onDuplicate);
      const { data } = await api.post(`/import-export/${module.key}/import`, fd);
      setImportResult(data);
      toast.success(`Import selesai: +${data.inserted} baru, ~${data.updated} diperbarui, ${data.skipped} dilewati`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.issues) {
        setPreviewData((p) => ({ ...(p || {}), invalid: detail.issues.length, issues: detail.issues }));
        toast.error(detail.message || "Import dibatalkan");
      } else {
        toast.error(formatApiError(detail) || "Gagal import");
      }
    } finally {
      setBusy(null);
    }
  };

  const filterOptions = module.hasFilters || [];

  return (
    <Dialog open onOpenChange={(o) => !o && closeAll()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid={`ie-dialog-${module.key}`}>
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-7 w-7 -ml-1" onClick={closeAll} data-testid="ie-back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <module.icon className="h-5 w-5 text-emerald-600" />
            {module.label}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Unduh template · Import Excel · Export ke Excel/CSV/PDF
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          {/* Action row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button
              variant="outline"
              className="justify-start h-auto py-3"
              onClick={downloadTemplate}
              disabled={busy === "template"}
              data-testid="ie-btn-template"
            >
              {busy === "template" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2 text-emerald-600" />}
              <div className="text-left">
                <div className="font-semibold text-sm">Download Template</div>
                <div className="text-[11px] text-slate-500">Format Excel resmi SiMadin</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="justify-start h-auto py-3 border-emerald-200 bg-emerald-50/50 hover:bg-emerald-50"
              onClick={() => inputRef.current?.click()}
              disabled={busy === "preview"}
              data-testid="ie-btn-import"
            >
              {busy === "preview" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2 text-emerald-700" />}
              <div className="text-left">
                <div className="font-semibold text-sm">Import Excel</div>
                <div className="text-[11px] text-slate-500">Pratinjau otomatis sebelum simpan</div>
              </div>
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => onFilePicked(e.target.files?.[0])}
              data-testid="ie-file-input"
            />

            <div className="border rounded-md flex flex-col p-3 gap-2" data-testid="ie-export-box">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <FileText className="h-4 w-4 text-emerald-600" />
                Export Data
              </div>
              <div className="grid grid-cols-3 gap-1">
                {["xlsx", "csv", "pdf"].map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setExportFormat(f)}
                    data-testid={`ie-fmt-${f}`}
                    className={`text-xs py-1 rounded border ${
                      exportFormat === f
                        ? "bg-emerald-600 text-white border-emerald-600"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {f.toUpperCase()}
                  </button>
                ))}
              </div>
              <Button
                size="sm"
                onClick={doExport}
                disabled={busy === "export"}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                data-testid="ie-btn-export"
              >
                {busy === "export" ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                Unduh
              </Button>
            </div>
          </div>

          {/* Export filters */}
          {filterOptions.length > 0 && (
            <div className="bg-slate-50/60 border border-slate-200 rounded-lg p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Filter Export (opsional)
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {filterOptions.includes("tahun_ajaran") && (
                  <FilterSelect
                    label="Tahun Ajaran"
                    testId="filter-ta"
                    value={exportFilters.tahun_ajaran || "all"}
                    options={[{ value: "all", label: "Semua" }, ...tahunList.map((t) => ({ value: t.tahun_ajaran, label: t.tahun_ajaran }))]}
                    onChange={(v) => setExportFilters((f) => ({ ...f, tahun_ajaran: v }))}
                  />
                )}
                {filterOptions.includes("kelas") && (
                  <FilterSelect
                    label="Kelas"
                    testId="filter-kelas"
                    value={exportFilters.kelas_id || "all"}
                    options={[{ value: "all", label: "Semua" }, ...kelasList.map((k) => ({ value: k.id, label: k.nama }))]}
                    onChange={(v) => setExportFilters((f) => ({ ...f, kelas_id: v }))}
                  />
                )}
                {filterOptions.includes("status") && (
                  <FilterSelect
                    label="Status"
                    testId="filter-status"
                    value={exportFilters.status || "all"}
                    options={
                      module.key === "santri"
                        ? [
                            { value: "all", label: "Semua" },
                            { value: "aktif", label: "Aktif" },
                            { value: "nonaktif", label: "Non-aktif" },
                            { value: "lulus", label: "Lulus" },
                          ]
                        : [
                            { value: "all", label: "Semua" },
                            { value: "aktif", label: "Aktif" },
                            { value: "nonaktif", label: "Non-aktif" },
                          ]
                    }
                    onChange={(v) => setExportFilters((f) => ({ ...f, status: v }))}
                  />
                )}
                {filterOptions.includes("jenis_kelamin") && (
                  <FilterSelect
                    label="Jenis Kelamin"
                    testId="filter-jk"
                    value={exportFilters.jenis_kelamin || "all"}
                    options={[
                      { value: "all", label: "Semua" },
                      { value: "L", label: "Laki-laki" },
                      { value: "P", label: "Perempuan" },
                    ]}
                    onChange={(v) => setExportFilters((f) => ({ ...f, jenis_kelamin: v }))}
                  />
                )}
              </div>
            </div>
          )}

          {selectedFile && (
            <div className="text-xs text-slate-500 flex items-center gap-2 border-t pt-4">
              <FileType className="h-4 w-4" />
              File dipilih: <span className="font-medium text-slate-800">{selectedFile.name}</span>
              <span>·</span>
              <span>{Math.round(selectedFile.size / 1024)} KB</span>
            </div>
          )}

          {previewData && (
            <PreviewPanel data={previewData} onDuplicate={onDuplicate} setOnDuplicate={setOnDuplicate} />
          )}

          {importResult && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm" data-testid="ie-import-result">
              <div className="font-semibold text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Import selesai
              </div>
              <div className="mt-2 grid grid-cols-3 gap-3 text-center">
                <div><div className="text-2xl font-bold text-emerald-700">{importResult.inserted}</div><div className="text-xs text-emerald-700/80">Baru</div></div>
                <div><div className="text-2xl font-bold text-amber-700">{importResult.updated}</div><div className="text-xs text-amber-700/80">Diperbarui</div></div>
                <div><div className="text-2xl font-bold text-slate-500">{importResult.skipped}</div><div className="text-xs text-slate-500">Dilewati</div></div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-slate-100 px-6 py-3 bg-slate-50/40 flex-shrink-0">
          <Button variant="outline" onClick={closeAll} data-testid="ie-btn-close">Tutup</Button>
          {previewData && !importResult && (
            <Button
              onClick={runImport}
              disabled={busy === "import" || (previewData.invalid || 0) > 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="ie-btn-run-import"
            >
              {busy === "import" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Simpan Import ({previewData.valid} valid)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilterSelect({ label, options, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 h-9" data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PreviewPanel({ data, onDuplicate, setOnDuplicate }) {
  const hasErrors = (data.invalid || 0) > 0;
  const hasDuplicates = (data.duplicates || 0) > 0;
  const headers = data.headers || [];
  const preview = data.preview || [];
  const issuesMap = useMemo(() => {
    const map = {};
    for (const it of data.issues || []) map[it.row] = it.errors;
    return map;
  }, [data.issues]);

  return (
    <div className="space-y-4" data-testid="ie-preview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatChip label="Total baris" value={data.total_rows} tone="slate" />
        <StatChip label="Valid" value={data.valid} tone="emerald" />
        <StatChip label="Bermasalah" value={data.invalid} tone="red" />
        <StatChip label="Sudah ada" value={data.duplicates} tone="amber" />
      </div>

      {hasErrors && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4" data-testid="ie-errors">
          <div className="flex items-center gap-2 text-red-800 font-semibold text-sm">
            <XCircle className="h-4 w-4" />
            Ada {data.invalid} baris yang perlu diperbaiki (import diblokir)
          </div>
          <div className="mt-2 max-h-48 overflow-y-auto text-xs space-y-1">
            {(data.issues || []).map((it) => (
              <div key={it.row} className="flex items-start gap-2 text-red-700">
                <Badge variant="outline" className="border-red-300 text-red-700 bg-white shrink-0">Baris {it.row}</Badge>
                <span>{it.errors.join(", ")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasDuplicates && !hasErrors && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-4" data-testid="ie-duplicates">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
            <AlertTriangle className="h-4 w-4" />
            {data.duplicates} data sudah ada di database — pilih tindakan:
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setOnDuplicate("skip")}
              data-testid="ie-dup-skip"
              className={`text-xs px-3 py-1.5 rounded-full border ${
                onDuplicate === "skip"
                  ? "bg-amber-600 text-white border-amber-600"
                  : "bg-white border-amber-300 text-amber-800 hover:bg-amber-100"
              }`}
            >
              Lewati (tidak menimpa data lama)
            </button>
            <button
              type="button"
              onClick={() => setOnDuplicate("update")}
              data-testid="ie-dup-update"
              className={`text-xs px-3 py-1.5 rounded-full border ${
                onDuplicate === "update"
                  ? "bg-amber-600 text-white border-amber-600"
                  : "bg-white border-amber-300 text-amber-800 hover:bg-amber-100"
              }`}
            >
              Perbarui data lama
            </button>
          </div>
        </div>
      )}

      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
          Pratinjau data ({preview.length} baris pertama)
        </div>
        <div className="border rounded-lg overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="ie-preview-table">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="px-2 py-2 text-left font-semibold text-slate-500">#</th>
                <th className="px-2 py-2 text-left font-semibold text-slate-500">Status</th>
                {headers.map((h) => (
                  <th key={h} className="px-2 py-2 text-left font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.map((p) => (
                <tr
                  key={p.row}
                  className={
                    (p.errors || []).length
                      ? "bg-red-50/40"
                      : p.duplicate_in_db
                        ? "bg-amber-50/40"
                        : ""
                  }
                >
                  <td className="px-2 py-1.5 text-slate-500">{p.row}</td>
                  <td className="px-2 py-1.5">
                    {(p.errors || []).length ? (
                      <span className="text-red-700 font-medium">Error</span>
                    ) : p.duplicate_in_db ? (
                      <span className="text-amber-700 font-medium">Duplikat</span>
                    ) : (
                      <span className="text-emerald-700 font-medium">Valid</span>
                    )}
                  </td>
                  {(p.raw || []).map((cell, idx) => (
                    <td key={idx} className="px-2 py-1.5 whitespace-nowrap text-slate-700 max-w-[180px] truncate">
                      {cell === "" || cell === null || cell === undefined ? <span className="text-slate-300">—</span> : String(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {issuesMap && Object.keys(issuesMap).length > 0 && (
          <p className="text-[11px] text-slate-400 mt-1">Baris berwarna merah = ada error. Baris kuning = data sudah ada di database.</p>
        )}
      </div>
    </div>
  );
}

function StatChip({ label, value, tone }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    red: "bg-red-50 text-red-800 border-red-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone] || tones.slate}`}>
      <div className="font-heading text-2xl font-bold tabular-nums">{value ?? 0}</div>
      <div className="text-[11px] uppercase tracking-wider opacity-80 mt-0.5">{label}</div>
    </div>
  );
}
